import { parse, format, eachDayOfInterval, isSameDay, isWithinInterval } from "date-fns";

export const processApiResponse = (response, setWeeklyData, setOverallData, setGoals) => {
  if (!response || !response.weekly_summaries || response.weekly_summaries.length === 0) {
    return;
  }

  const sortedWeeklyData = [...response.weekly_summaries].sort((a, b) => {
    const dateA = parse(a.week_start, "yyyy-MM-dd", new Date());
    const dateB = parse(b.week_start, "yyyy-MM-dd", new Date());
    return dateB - dateA;
  });

  setWeeklyData(sortedWeeklyData);
  setOverallData(response.overall_summary || response.overall);

  if (response.goals) {
    setGoals(response.goals);
  } else if (response.overall_summary?.goals) {
    setGoals(response.overall_summary.goals);
  }

  return sortedWeeklyData;
};

export const processWeekData = (weekData, setCurrentWeekRange, setCalendarDate, setChartData, processSummaryStats) => {
  if (!weekData) return;

  // Update current week range
  if (weekData.week_start && weekData.week_end) {
    const startDate = parse(weekData.week_start, "yyyy-MM-dd", new Date());
    const endDate = parse(weekData.week_end, "yyyy-MM-dd", new Date());
    setCurrentWeekRange({ startDate, endDate });
    setCalendarDate(startDate);
  }

  // Process chart data
  if (weekData.summary?.graph_data) {
    const graphData = weekData.summary.graph_data;
    
    setChartData({
      steps: graphData.steps?.map(item => ({
        name: item.name,
        steps: item.value || 0,
      })) || [],
      calories: graphData.calories?.map(item => ({
        name: item.name,
        calories: item.value ? Math.round(item.value) : 0,
      })) || [],
      activeMinutes: graphData.active_minutes?.map(item => ({
        name: item.name,
        activeMinutes: item.value || 0,
      })) || [],
    });
  }

  // Process summary stats
  if (weekData.summary) {
    processSummaryStats(weekData.summary);
  }
};

export const processSummaryStats = (summary, setSummaryStats) => {
  if (!summary) {
    setDefaultSummaryStats(setSummaryStats);
    return;
  }

  const formatActiveTime = (minutes) => {
    if (!minutes) return "0m";
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatCalories = (calories) => {
    if (!calories) return "0 kcal";
    return `${parseFloat(calories).toFixed(0)} kcal`;
  };

  const formatDistance = (distance) => {
    if (!distance) return "0 m";
    if (distance >= 1000) {
      return `${(distance / 1000).toFixed(2)} km`;
    }
    return `${Math.round(distance)} m`;
  };

  setSummaryStats({
    totalSteps: summary.totals?.steps?.toLocaleString() || "0",
    caloriesBurnt: formatCalories(summary.totals?.calories) || "0 kcal",
    activeTime: formatActiveTime(summary.totals?.active_minutes) || "0m",
    distance: summary?.totals?.distance
      ? (summary.totals.distance / 1000).toFixed(2) + " km"
      : "0 km",
    progressPercentage: summary.weekly_goal_progress || "0",
    weeklyGoalProgress: summary.weekly_goal_progress || "0",
    totalDistance: formatDistance(summary.totals?.distance) || "0 m",
    averageSteps: summary.averages?.steps?.toLocaleString() || "0",
    averageCalories: formatCalories(summary.averages?.calories) || "0 kcal",
    averageActiveTime: formatActiveTime(summary.averages?.active_minutes) || "0m",
    fat_free_mass_kg: summary.averages?.fat_free_mass_kg != null
      ? `${summary.averages.fat_free_mass_kg} kg`
      : "0 kg",
    sleep_minutes: summary.averages?.sleep_minutes != null
      ? `${summary.averages.sleep_minutes}`
      : "0h 0m",
    weight: summary.averages?.weight != null
      ? `${summary.averages.weight} kg`
      : "0 kg",
    muscle_mass_kg: summary.averages?.muscle_mass_kg != null
      ? `${summary.averages.muscle_mass_kg} kg`
      : "0 kg",
    body_fat_percentage: summary.averages?.body_fat_percentage != null
      ? `${summary.averages.body_fat_percentage}%`
      : "0 %",
    bmi: summary.averages?.bmi ?? "0",
    blood_pressure: `${
      summary.averages?.blood_pressure_systolic ?? 0
    }/${summary.averages?.blood_pressure_diastolic ?? 0} mmHg`,
    heart_rate: summary.averages?.avg_heart_rate != null
      ? `${summary.averages.avg_heart_rate} bpm`
      : "0 bpm",
  });
};

const setDefaultSummaryStats = (setSummaryStats) => {
  setSummaryStats({
    totalSteps: "0",
    caloriesBurnt: "0 kcal",
    activeTime: "0m",
    distance: "0 km",
    progressPercentage: "0",
    weeklyGoalProgress: "0",
    totalDistance: "0 m",
    averageSteps: "0",
    averageCalories: "0 kcal",
    averageActiveTime: "0m",
    fat_free_mass_kg: "0 kg",
    sleep_minutes: "0h 0m",
    weight: "0 kg",
    muscle_mass_kg: "0 kg",
    body_fat_percentage: "0 %",
    bmi: "0",
    blood_pressure: "0/0 mmHg",
    heart_rate: "0 bpm",
  });
};

export const getEnabledDates = (weeklyData) => {
  const enabledDates = [];
  weeklyData.forEach((week) => {
    if (week.week_start && week.week_end) {
      const start = parse(week.week_start, "yyyy-MM-dd", new Date());
      const end = parse(week.week_end, "yyyy-MM-dd", new Date());
      const daysInWeek = eachDayOfInterval({ start, end });
      enabledDates.push(...daysInWeek);
    }
  });
  return enabledDates;
};

export const isDateEnabled = (date, weeklyData) => {
  const enabledDates = getEnabledDates(weeklyData);
  return enabledDates.some((enabledDate) => isSameDay(enabledDate, date));
};

export const findWeekIndexForDate = (date, weeklyData) => {
  return weeklyData.findIndex((week) => {
    if (week.week_start && week.week_end) {
      const start = parse(week.week_start, "yyyy-MM-dd", new Date());
      const end = parse(week.week_end, "yyyy-MM-dd", new Date());
      return isWithinInterval(date, { start, end });
    }
    return false;
  });
};